"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    zip: "",
    project_type: "",
    timing: "",
    budget: "",
    message: "",
    consent: false
  });
  const [zipError, setZipError] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === "zip" && zipError) {
      if (/^\d{5}$/.test(value.trim())) {
        setZipError(null);
      }
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    const isZipValid = /^\d{5}$/.test(formData.zip.trim());
    if (!isZipValid) {
      setZipError("Please enter a valid 5-digit ZIP code.");
    } else {
      setZipError(null);
    }
    if (!formData.consent) {
      setConsentError("Please provide your consent to continue.");
    } else {
      setConsentError(null);
    }
    if (!isZipValid || !formData.consent) {
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        project_type: formData.project_type,
        address: formData.zip,
        zip: formData.zip,
        details: formData.message,
        timing: formData.timing,
        budget: formData.budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "service-areas-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Architectural Grid Lines */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-40 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header Block */}
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">
            Project Inquiry
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-4">
            Request an Estimate
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            Tell us about your home and your renovation vision. Our team will review your project details and follow up for an initial consultation.
          </p>
        </div>

        {/* Form Container Card */}
        <div className="rounded-2xl border border-border/80 bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 md:p-12 transition-all">
          {isSuccess ? <div className="py-12 text-center flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-primary/20 text-primary flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground mb-3">
                Estimate Request Received
              </h3>
              <p className="text-muted-foreground text-base max-w-md mx-auto mb-8 leading-relaxed">
                Thank you for reaching out. We have received your project details and will follow up to discuss your project.
              </p>
              <Button variant="outline" onClick={() => {
            setIsSuccess(false);
            setFormData({
              name: "",
              phone: "",
              email: "",
              zip: "",
              project_type: "",
              timing: "",
              budget: "",
              message: "",
              consent: false
            });
          }}>
                Submit another inquiry
              </Button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && <Alert variant="destructive" className="border-destructive/30">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Submission Notice</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              {/* Row 1: Full Name & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="field-name" className="text-foreground">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-name" name="name" type="text" required placeholder="Jane Smith" value={formData.name} onChange={handleInputChange} className="bg-background/80" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-phone" className="text-foreground">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-phone" name="phone" type="tel" required placeholder="(404) 555-0199" value={formData.phone} onChange={handleInputChange} className="bg-background/80" />
                </div>
              </div>

              {/* Row 2: Email & ZIP */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="field-email" className="text-foreground">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-email" name="email" type="email" required placeholder="jane@example.com" value={formData.email} onChange={handleInputChange} className="bg-background/80" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-zip" className="text-foreground">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-zip" name="zip" type="text" required maxLength={5} placeholder="30319" value={formData.zip} onChange={handleInputChange} aria-invalid={!!zipError} className="bg-background/80" />
                  {zipError && <p className="text-xs text-destructive mt-1 font-medium">
                      {zipError}
                    </p>}
                </div>
              </div>

              {/* Row 3: Project Type (Required Select) */}
              <div className="space-y-2">
                <Label htmlFor="field-project-type" className="text-foreground">
                  Project Type <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <select id="field-project-type" name="project_type" required value={formData.project_type} onChange={handleInputChange} className="border-input flex h-9 w-full rounded-md border bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none text-foreground appearance-none cursor-pointer">
                    <option value="" disabled>
                      Select project type
                    </option>
                    {PROJECT_TYPE_OPTIONS.map((opt, i) => <option key={i} value={opt} data-index={i}>
                        {opt}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                    <span className="text-xs">▼</span>
                  </div>
                </div>
              </div>

              {/* Row 4: Timing & Budget (Optional Selects) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="field-timing" className="text-foreground">
                    Preferred Timing
                  </Label>
                  <div className="relative">
                    <select id="field-timing" name="timing" value={formData.timing} onChange={handleInputChange} className="border-input flex h-9 w-full rounded-md border bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none text-foreground appearance-none cursor-pointer">
                      <option value="">Select timeframe (optional)</option>
                      {TIMING_OPTIONS.map((timing, i) => <option key={i} value={timing} data-index={i}>
                          {timing}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                      <span className="text-xs">▼</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-budget" className="text-foreground">
                    Budget Range
                  </Label>
                  <div className="relative">
                    <select id="field-budget" name="budget" value={formData.budget} onChange={handleInputChange} className="border-input flex h-9 w-full rounded-md border bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none text-foreground appearance-none cursor-pointer">
                      <option value="">Select budget range (optional)</option>
                      {BUDGET_OPTIONS.map((budget, i) => <option key={i} value={budget} data-index={i}>
                          {budget}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                      <span className="text-xs">▼</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Message / Project Details */}
              <div className="space-y-2">
                <Label htmlFor="field-message" className="text-foreground">
                  Project Details / Message
                </Label>
                <Textarea id="field-message" name="message" rows={4} placeholder="Share any scope specifics, architectural ideas, or questions about your home..." value={formData.message} onChange={handleInputChange} className="bg-background/80 resize-y" />
              </div>

              {/* Consent Checkbox */}
              <div className="pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => {
                const isChecked = checked === true;
                setFormData(prev => ({
                  ...prev,
                  consent: isChecked
                }));
                if (isChecked) setConsentError(null);
              }} className="mt-0.5" />
                  <div className="grid gap-1.5 leading-none">
                    <Label htmlFor="consent" className="text-xs sm:text-sm font-normal text-muted-foreground cursor-pointer leading-relaxed">
                      I agree to be contacted about my project by phone, text, or email.
                    </Label>
                  </div>
                </div>
                {consentError && <p className="text-xs text-destructive mt-1.5 font-medium ml-7">
                    {consentError}
                  </p>}
              </div>

              {/* Submit CTA */}
              <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto h-auto min-w-[200px] bg-primary text-primary-foreground font-semibold py-3.5 px-8 rounded-lg shadow-md hover:bg-primary/90 transition-all flex items-center justify-center gap-2">
                  {isSubmitting ? <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </> : <>
                      <span>Submit Estimate Request</span>
                      <ArrowRight className="w-4 h-4" />
                    </>}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}